// Keep email markup independent from the admin stylesheet and safe to insert as HTML.
export function tableOptions({ rows, columns, header = true, borders = true, headerColor = '#f4f4f5', borderColor = '#d4d4d8', backgroundColor = '#ffffff', textColor = '#27272a', headerTextColor = '#27272a', borderWidth = 1, borderStyle = 'solid' }) {
    rows = Number(rows);
    columns = Number(columns);
    if (!Number.isInteger(rows) || rows < 1 || rows > 20 || !Number.isInteger(columns) || columns < 1 || columns > 6) {
        throw new Error('Indicá entre 1 y 20 filas y entre 1 y 6 columnas.');
    }
    if (![headerColor, borderColor, backgroundColor, textColor, headerTextColor].every(color => /^#[0-9a-f]{6}$/i.test(color))) {
        throw new Error('Elegí colores válidos para la tabla.');
    }
    borderWidth = Number(borderWidth);
    if (!Number.isInteger(borderWidth) || borderWidth < 1 || borderWidth > 5 || !['solid', 'dashed', 'dotted', 'double'].includes(borderStyle)) {
        throw new Error('Elegí un borde de 1 a 5 px y un estilo válido.');
    }
    return { rows, columns, header, borders, headerColor, borderColor, backgroundColor, textColor, headerTextColor, borderWidth, borderStyle };
}

export function buildCommunicationTable(options) {
    const { rows, columns, header, borders, headerColor, borderColor, backgroundColor, textColor, headerTextColor, borderWidth, borderStyle } = tableOptions(options);
    const width = Number((100 / columns).toFixed(4));
    const cellStyle = `width:${width}%;padding:10px;text-align:left;vertical-align:top;border:${borders ? `${borderWidth}px ${borderStyle} ${borderColor}` : '0'};`;
    const row = isHeader => '<tr>' + Array.from({ length: columns }, (_, index) => {
        const tag = isHeader ? 'th' : 'td';
        return `<${tag}${isHeader ? ' scope="col"' : ''} width="${width}%" style="${cellStyle}background-color:${isHeader ? headerColor : backgroundColor};color:${isHeader ? headerTextColor : textColor};${isHeader ? 'font-weight:bold;' : ''}">${isHeader ? `Encabezado ${index + 1}` : 'Texto'}</${tag}>`;
    }).join('') + '</tr>';
    const heading = header ? `<thead>${row(true)}</thead>` : '';
    const bodyRows = rows - (header ? 1 : 0);
    const body = bodyRows ? `<tbody>${Array.from({ length: bodyRows }, () => row(false)).join('')}</tbody>` : '';
    return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:100%;border-collapse:collapse;table-layout:fixed;">${heading}${body}</table><p><br></p>`;
}

function simpleTable(table) {
    const rows = [...table.rows];
    const columns = rows[0]?.cells.length;
    if (!rows.length || rows.length > 20 || !columns || columns > 6 || table.querySelector('table, colgroup, tfoot') || rows.some(row => row.cells.length !== columns || [...row.cells].some(cell => cell.rowSpan !== 1 || cell.colSpan !== 1))) {
        throw new Error('Esta tabla tiene una estructura compleja o supera 20 filas / 6 columnas. Modificala desde HTML para conservar su diseño.');
    }
    return rows;
}

const hexColor = (color, fallback) => {
    if (/^#[0-9a-f]{6}$/i.test(color)) return color;
    const parts = color?.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
    return parts && (!parts[4] || Number(parts[4]) === 1) ? '#' + parts.slice(1, 4).map(n => Number(n).toString(16).padStart(2, '0')).join('') : fallback;
};

export function readCommunicationTable(table) {
    const rows = simpleTable(table);
    const header = [...rows[0].cells].every(cell => cell.tagName === 'TH');
    const first = getComputedStyle(rows[0].cells[0]);
    const body = getComputedStyle(rows[header && rows.length > 1 ? 1 : 0].cells[0]);
    return {
        rows: rows.length, columns: rows[0].cells.length, header,
        borders: first.borderTopStyle !== 'none' && parseFloat(first.borderTopWidth) > 0,
        borderWidth: Math.max(1, Math.min(5, Math.round(parseFloat(first.borderTopWidth) || 1))),
        borderStyle: ['solid', 'dashed', 'dotted', 'double'].includes(first.borderTopStyle) ? first.borderTopStyle : 'solid',
        borderColor: hexColor(first.borderTopColor, '#d4d4d8'),
        headerColor: header ? hexColor(first.backgroundColor, '#f4f4f5') : '#f4f4f5',
        headerTextColor: hexColor(first.color, '#27272a'),
        backgroundColor: hexColor(body.backgroundColor, '#ffffff'), textColor: hexColor(body.color, '#27272a'),
    };
}

export function updateCommunicationTable(table, options, allowRemoval = false) {
    const oldRows = simpleTable(table);
    const settings = tableOptions(options);
    if ((settings.rows < oldRows.length || settings.columns < oldRows[0].cells.length) && !allowRemoval) {
        throw new Error('Confirmá la eliminación de las celdas al reducir filas o columnas.');
    }
    const template = document.createElement('template');
    template.innerHTML = buildCommunicationTable(settings);
    const next = template.content.querySelector('table');
    // Work on a detached copy. Cancel/errors must never change the original.
    const result = table.cloneNode(true);
    [...result.children].filter(node => node.tagName !== 'CAPTION').forEach(node => node.remove());
    for (const attr of [...next.attributes]) result.setAttribute(attr.name, attr.value);
    [...next.rows].forEach((row, rowIndex) => [...row.cells].forEach((cell, columnIndex) => {
        const oldCell = oldRows[rowIndex]?.cells[columnIndex];
        if (!oldCell) return;
        const controlled = Object.fromEntries(['width', 'border', 'background-color', 'color'].map(property => [property, cell.style.getPropertyValue(property)]));
        cell.innerHTML = oldCell.innerHTML;
        for (const attr of [...oldCell.attributes]) {
            if (!['style', 'width', 'scope', 'rowspan', 'colspan', 'bgcolor'].includes(attr.name)) cell.setAttribute(attr.name, attr.value);
        }
        // Preserve links, images, inline formatting and unrelated cell styles.
        cell.style.cssText = oldCell.style.cssText;
        for (const [property, value] of Object.entries(controlled)) cell.style.setProperty(property, value);
        if (!cell.style.padding) cell.style.padding = '10px';
        if (!cell.style.verticalAlign) cell.style.verticalAlign = 'top';
        if (cell.tagName !== oldCell.tagName) cell.style.fontWeight = cell.tagName === 'TH' ? 'bold' : 'normal';
    }));
    result.append(...next.childNodes);
    return result.outerHTML;
}
